import { useEffect, useMemo, useState } from 'react';
import type {
  ControlAction,
  LiveCommand,
  LiveDevicePhase,
  LiveSample,
  LiveStatus,
} from '@ccs/live-contract';
import { formatLogTime } from '../../../shared/format';
import { describeCommand, describeCommandError } from '../lib/command-text';

type Props = {
  phase: LiveDevicePhase;
  status: LiveStatus | null;
  sample: LiveSample | null;
  canControl: boolean;
  commands: LiveCommand[];
  busy: boolean;
  error: string | null;
  onCommand: (action: ControlAction, params: Record<string, unknown>) => Promise<void>;
};

const COMMAND_LABEL: Record<string, string> = {
  queued: '排队',
  sent: '已下发',
  confirmed: '已确认',
  unconfirmed: '未确认',
  rejected: '已拒绝',
  expired: '已过期',
  superseded: '已合并',
};

function commandClass(status: string): string {
  if (status === 'confirmed') return 'ok';
  if (status === 'rejected' || status === 'expired') return 'danger';
  if (status === 'unconfirmed') return 'warn';
  return '';
}

function RelayRow({
  label,
  on,
  onStart,
  onStop,
  disabled,
  note,
}: {
  label: string;
  on: boolean;
  onStart: () => void;
  onStop: () => void;
  disabled: boolean;
  note?: string;
}) {
  return (
    <div className="control-row">
      <span className={`control-dot${on ? ' on' : ''}`} aria-hidden />
      <div className="control-name">
        <b>{label}</b>
        <span>{note ?? (on ? '运行中' : '已停止')}</span>
      </div>
      <div className="control-actions">
        <button type="button" className="btn btn-sm" disabled={disabled || on} onClick={onStart}>
          启动
        </button>
        <button type="button" className="btn btn-ghost btn-sm" disabled={disabled || !on} onClick={onStop}>
          停止
        </button>
      </div>
    </div>
  );
}

function InverterRow({
  label,
  actualHz,
  mode,
  targetHz,
  onTargetHz,
  onStartFreq,
  onStartPid,
  onStop,
  disabled,
  pid,
}: {
  label: string;
  actualHz: number | null;
  mode: string;
  targetHz: number;
  onTargetHz: (value: number) => void;
  onStartFreq: () => void;
  onStartPid?: () => void;
  onStop: () => void;
  disabled: boolean;
  pid?: boolean;
}) {
  return (
    <div className="control-row control-row-inverter">
      <div className="control-name">
        <b>{label}</b>
        <span>
          回读 {actualHz === null ? '—' : `${actualHz.toFixed(1)} Hz`} · 模式 {mode === 'pid' ? 'PID' : mode === 'freq' ? '频率' : '停止'}
        </span>
      </div>
      <label className="control-number">
        <span>目标</span>
        <input
          className="input"
          type="number"
          min={0}
          max={50}
          step={0.1}
          value={targetHz}
          onChange={(event) => onTargetHz(Number(event.target.value))}
        />
        <em>Hz</em>
      </label>
      <div className="control-actions">
        <button type="button" className="btn btn-sm" disabled={disabled} onClick={onStartFreq}>
          频率启动
        </button>
        {pid ? (
          <button type="button" className="btn btn-sm" disabled={disabled} onClick={onStartPid}>
            PID 启动
          </button>
        ) : null}
        <button type="button" className="btn btn-ghost btn-sm" disabled={disabled} onClick={onStop}>
          停止
        </button>
      </div>
    </div>
  );
}

export default function LiveControlPanel({
  phase,
  status,
  sample,
  canControl,
  commands,
  busy,
  error,
  onCommand,
}: Props) {
  const desired = status?.control?.desired ?? null;
  const values = sample?.values ?? {};
  const dev0 = Number(values.dev0) || 0;
  const dev1 = Number(values.dev1) || 0;
  const bit = (value: number, index: number) => (value & (1 << index)) !== 0;

  const [poorHz, setPoorHz] = useState(45);
  const [poorSetpoint, setPoorSetpoint] = useState(120);
  const [richHz, setRichHz] = useState(50);
  const [airHz, setAirHz] = useState(30);
  const [airSetpoint, setAirSetpoint] = useState(12);
  const [heatEnabled, setHeatEnabled] = useState(false);
  const [heatUp, setHeatUp] = useState(40.2);
  const [heatDown, setHeatDown] = useState(40);

  useEffect(() => {
    if (!desired) return;
    setPoorHz(desired.inverters.poorPump.hz || 45);
    setRichHz(desired.inverters.richPump.hz || 50);
    setAirHz(desired.inverters.air.hz || 30);
    setPoorSetpoint(desired.inverters.poorPump.setpoint ?? 120);
    setAirSetpoint(desired.inverters.air.setpoint ?? 12);
    setHeatEnabled(desired.heating.enabled);
    setHeatUp(desired.heating.upperC);
    setHeatDown(desired.heating.lowerC);
  }, [
    desired?.inverters.poorPump.hz,
    desired?.inverters.richPump.hz,
    desired?.inverters.air.hz,
    desired?.inverters.poorPump.setpoint,
    desired?.inverters.air.setpoint,
    desired?.heating.enabled,
    desired?.heating.upperC,
    desired?.heating.lowerC,
  ]);

  const bridgeReady = phase === 'live' || phase === 'idle';
  const disabledReason = useMemo(() => {
    if (!canControl) return '当前账号没有控制权限';
    if (status?.control?.maintenance) return '维护锁已开启，远程控制暂停';
    if (status?.control?.enabled === false) return '管理员已关闭这台设备的控制';
    if (status?.stm32 && status.stm32 !== 'ok') return 'STM32 串口未连接，控制暂不可用';
    if (!bridgeReady) return '工控机不在线或数据陈旧';
    if (phase === 'idle') return '采集尚未开始；可先点「开始采集」';
    return null;
  }, [bridgeReady, canControl, phase, status?.control?.enabled, status?.control?.maintenance]);

  const disabled = Boolean(disabledReason && !(phase === 'idle' && canControl && !status?.control?.maintenance && status?.control?.enabled !== false)) || busy;
  const deviceDisabled = disabled || phase !== 'live';
  const actualPoor = typeof values.freq_poor === 'number' ? values.freq_poor : null;
  const actualRich = typeof values.freq_rich === 'number' ? values.freq_rich : null;
  const actualAir = typeof values.freq_air === 'number' ? values.freq_air : null;

  const acqRunning = status?.acq === 'running';
  const recent = commands.slice(0, 6);

  return (
    <section className="panel live-control-card">
      <div className="panel-head">
        <div>
          <div className="eyebrow">Control</div>
          <h2>控制面板</h2>
        </div>
        {disabledReason ? (
          <span className="badge warn">{disabledReason}</span>
        ) : (
          <span className="badge ok">可控制</span>
        )}
      </div>

      {error ? <div className="control-error">{describeCommandError(error)}</div> : null}

      <div className="control-grid">
        <div className="control-group">
          <h3>冷却水系统</h3>
          <RelayRow
            label="塔风机"
            on={bit(dev0, 3)}
            disabled={deviceDisabled}
            onStart={() => onCommand('relay.set', { device: 'tower_fan', on: true })}
            onStop={() => onCommand('relay.set', { device: 'tower_fan', on: false })}
          />
          <RelayRow
            label="塔水泵"
            on={bit(dev0, 4)}
            disabled={deviceDisabled}
            onStart={() => onCommand('relay.set', { device: 'tower_pump', on: true })}
            onStop={() => onCommand('relay.set', { device: 'tower_pump', on: false })}
          />
          <RelayRow
            label="循环水泵"
            on={bit(dev0, 5)}
            disabled={deviceDisabled}
            onStart={() => onCommand('relay.set', { device: 'loop_pump', on: true })}
            onStop={() => onCommand('relay.set', { device: 'loop_pump', on: false })}
          />
        </div>

        <div className="control-group">
          <h3>回流系统</h3>
          <RelayRow
            label="解吸塔回流泵"
            on={bit(dev1, 5)}
            disabled={deviceDisabled}
            onStart={() => onCommand('relay.set', { device: 'desorber_reflux', on: true })}
            onStop={() => onCommand('relay.set', { device: 'desorber_reflux', on: false })}
          />
          <RelayRow
            label="吸收塔回流泵"
            on={bit(dev1, 4)}
            disabled={deviceDisabled}
            onStart={() => onCommand('relay.set', { device: 'absorber_reflux', on: true })}
            onStop={() => onCommand('relay.set', { device: 'absorber_reflux', on: false })}
          />
          <RelayRow
            label="补液电磁阀"
            on={false}
            disabled={deviceDisabled}
            note="无状态回读；下发后只显示已下发"
            onStart={() => onCommand('relay.set', { device: 'makeup_valve', on: true })}
            onStop={() => onCommand('relay.set', { device: 'makeup_valve', on: false })}
          />
        </div>

        <div className="control-group control-group-wide">
          <h3>循环系统 · 变频器</h3>
          <InverterRow
            label="贫液泵"
            actualHz={actualPoor}
            mode={desired?.inverters.poorPump.mode ?? 'stopped'}
            targetHz={poorHz}
            onTargetHz={setPoorHz}
            disabled={deviceDisabled}
            pid
            onStartFreq={() =>
              onCommand('inverter.start', { target: 'poor_pump', mode: 'freq', hz: poorHz })
            }
            onStartPid={() =>
              onCommand('inverter.start', {
                target: 'poor_pump',
                mode: 'pid',
                setpoint: poorSetpoint,
                hz: 30,
              })
            }
            onStop={() => onCommand('inverter.stop', { target: 'poor_pump' })}
          />
          <div className="control-sub">
            <label>
              PID 流量设定
              <input
                className="input"
                type="number"
                min={0}
                max={1000}
                step={1}
                value={poorSetpoint}
                onChange={(event) => setPoorSetpoint(Number(event.target.value))}
              />
              <em>L/h</em>
            </label>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={deviceDisabled}
              onClick={() =>
                onCommand('inverter.set_setpoint', {
                  target: 'poor_pump',
                  setpoint: poorSetpoint,
                })
              }
            >
              更新设定
            </button>
          </div>
          <InverterRow
            label="富液泵"
            actualHz={actualRich}
            mode={desired?.inverters.richPump.mode ?? 'stopped'}
            targetHz={richHz}
            onTargetHz={setRichHz}
            disabled={deviceDisabled}
            onStartFreq={() =>
              onCommand('inverter.start', { target: 'rich_pump', mode: 'freq', hz: richHz })
            }
            onStop={() => onCommand('inverter.stop', { target: 'rich_pump' })}
          />
          <InverterRow
            label="鼓风机"
            actualHz={actualAir}
            mode={desired?.inverters.air.mode ?? 'stopped'}
            targetHz={airHz}
            onTargetHz={setAirHz}
            disabled={deviceDisabled}
            pid
            onStartFreq={() =>
              onCommand('inverter.start', { target: 'air', mode: 'freq', hz: airHz })
            }
            onStartPid={() =>
              onCommand('inverter.start', {
                target: 'air',
                mode: 'pid',
                setpoint: airSetpoint,
                hz: 30,
              })
            }
            onStop={() => onCommand('inverter.stop', { target: 'air' })}
          />
          <div className="control-sub">
            <label>
              PID 流量设定
              <input
                className="input"
                type="number"
                min={0}
                max={1000}
                step={0.1}
                value={airSetpoint}
                onChange={(event) => setAirSetpoint(Number(event.target.value))}
              />
              <em>m³/h</em>
            </label>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={deviceDisabled}
              onClick={() =>
                onCommand('inverter.set_setpoint', { target: 'air', setpoint: airSetpoint })
              }
            >
              更新设定
            </button>
          </div>
        </div>

        <div className="control-group">
          <h3>进气加热</h3>
          <div className="control-row">
            <span className={`control-dot${bit(dev0, 6) ? ' on' : ''}`} aria-hidden />
            <div className="control-name">
              <b>回差控制</b>
              <span>
                反馈 T1_2 {typeof values.T1_2 === 'number' ? `${values.T1_2.toFixed(2)} ℃` : '—'}
              </span>
            </div>
          </div>
          <div className="control-sub control-sub-heat">
            <label>
              下限
              <input
                className="input"
                type="number"
                step={0.1}
                value={heatDown}
                onChange={(event) => setHeatDown(Number(event.target.value))}
              />
              <em>℃</em>
            </label>
            <label>
              上限
              <input
                className="input"
                type="number"
                step={0.1}
                value={heatUp}
                onChange={(event) => setHeatUp(Number(event.target.value))}
              />
              <em>℃</em>
            </label>
            <label className="control-check">
              <input
                type="checkbox"
                checked={heatEnabled}
                onChange={(event) => setHeatEnabled(event.target.checked)}
              />
              启用
            </label>
            <button
              type="button"
              className="btn btn-sm"
              disabled={deviceDisabled}
              onClick={() =>
                onCommand('heating.configure', {
                  enabled: heatEnabled,
                  upper_c: heatUp,
                  lower_c: heatDown,
                })
              }
            >
              应用
            </button>
          </div>
        </div>

        <div className="control-group">
          <h3>采集与安全</h3>
          <div className="control-row">
            <div className="control-name">
              <b>采集会话</b>
              <span>{status?.acq === 'running' ? '正在采集与记录' : '未采集'}</span>
            </div>
            <div className="control-actions">
              <button
                type="button"
                className="btn btn-sm"
                disabled={disabled || acqRunning}
                onClick={() => onCommand('acq.start', {})}
              >
                开始采集
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={disabled || !acqRunning}
                onClick={() => {
                  if (window.confirm('停止采集会结束当前记录会话，但不会停止现场设备。继续？')) {
                    void onCommand('acq.stop', {});
                  }
                }}
              >
                停止采集
              </button>
            </div>
          </div>
          <div className="control-row">
            <div className="control-name">
              <b>全部停止</b>
              <span>继电器全关 + 三台变频器 0；远程停止有延迟，紧急情况请按现场物理急停</span>
            </div>
            <div className="control-actions">
              <button
                type="button"
                className="btn btn-danger btn-sm"
                disabled={disabled}
                onClick={() => {
                  if (window.confirm('确认执行全部停止？远程停止存在 2–8 秒延迟。')) {
                    void onCommand('all.stop', {});
                  }
                }}
              >
                全部停止
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="control-commands">
        <div className="control-commands-head">
          <b>最近命令</b>
          <span className="muted">每台设备限速 1 条 / 2 秒；固件队列约 0.5 条 / 秒</span>
        </div>
        {recent.length ? (
          <div className="control-command-list">
            {recent.map((command) => {
              const reason = describeCommandError(command.error);
              return (
                <div className="control-command" key={command.id}>
                  <span className={`badge ${commandClass(command.status)}`}>
                    {COMMAND_LABEL[command.status] ?? command.status}
                  </span>
                  <b>{describeCommand(command)}</b>
                  {reason ? (
                    <span className="control-command-error" title={command.error ?? undefined}>
                      {reason}
                    </span>
                  ) : null}
                  <span className="muted">
                    {command.userName ?? '—'} · {formatLogTime(command.issuedAt)}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="muted" style={{ fontSize: 12, paddingTop: 6 }}>
            还没有控制命令
          </div>
        )}
      </div>
    </section>
  );
}